import { useMutation, useQuery } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { InlineBanner } from '@/components/commons/inline-banner'
import { MarqueeBreadcrumbs } from '@/components/commons/marquee-breadcrumbs'
import { Button } from '@/components/ui/button'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle
} from '@/components/ui/empty'
import {
	AlertTriangleFillIcon,
	DownloadCloud02Icon,
	InfoFillCircleIcon,
	UploadIcon
} from '@/components/ui/icons'
import { Skeleton } from '@/components/ui/skeleton'
import { Spinner } from '@/components/ui/spinner'
import { CreatorOnlyNotice } from '@/features/marquee/components/creator-only-notice'
import { HostLookupStatusCard } from '@/features/marquee/components/host-lookup-status-card'
import { HostLookupUploadDialog } from '@/features/marquee/components/host-lookup-upload-dialog'
import {
	operatorQueryOptions,
	resolutionSourceQueryOptions
} from '@/features/marquee/query-options'
import {
	hostLookupTableState,
	isMarketManagerAllowed,
	saveCsv
} from '@/features/marquee/utils'
import { errorHandler } from '@/helpers/utils'
import { routes } from '@/routes'
import { exportResolutionSource } from '@/sdk'
import { useTranslation } from '@/translations/i18n'

/**
 * The name the backend uses for this file everywhere, including in its own
 * instruction for resetting the table ("upload the bundled
 * resolution-source.csv"). The download carries no `content-disposition`, so
 * the client picks the name, and picking the backend's keeps a downloaded file
 * recognisable as the thing that goes back up.
 */
const LOOKUP_TABLE_FILENAME = 'resolution-source.csv'

function PageShell({ children }: { children: React.ReactNode }) {
	const { t } = useTranslation()
	return (
		<div className='flex flex-1 flex-col'>
			<title>{t('marquee:page_title_host_lookup_table')}</title>
			<div className='border-neutral-200 border-b l:px-10 px-4 pt-6 pb-6'>
				<MarqueeBreadcrumbs />
			</div>
			{children}
		</div>
	)
}

/**
 * The global host ID to Salesforce account lookup: what is loaded, and the
 * download-edit-upload loop that changes it.
 *
 * One table for every event and every market, so onboarding a market is adding
 * that market's rows to it rather than a per-event setting. That is also why
 * download comes first and is a full-weight action rather than something in a
 * corner: an upload **replaces** the table, so the only safe edit starts from
 * the current file. A screen that offered upload alone would invite someone to
 * send their own market's rows and silently retire every other market's.
 *
 * Creator work throughout (FR51), the whole screen rather than the upload half:
 * one table serves every market, so a change here reaches events this operator
 * never works. The account-menu entry is already hidden for a facilitator, so
 * only a URL reaches this, and the permission is asked first: nothing about the
 * table is fetched for someone who may not touch it.
 */
function MarqueeHostLookupTable() {
	const { t } = useTranslation()

	const { data: operator } = useQuery(operatorQueryOptions())
	const isAllowedToUpload = isMarketManagerAllowed(
		operator,
		'uploadResolutionSource'
	)

	const {
		data: status,
		isLoading,
		isError
	} = useQuery({
		...resolutionSourceQueryOptions(),
		enabled: isAllowedToUpload
	})

	const fileInputRef = useRef<HTMLInputElement>(null)
	const [pendingFile, setPendingFile] = useState<File | null>(null)

	const download = useMutation({
		mutationFn: async () => {
			const response = await exportResolutionSource()
			return response.data
		},
		onSuccess: (csv) => saveCsv(LOOKUP_TABLE_FILENAME, csv),
		onError: (error) => errorHandler(error)
	})

	/**
	 * Clears the input's value before opening it, so picking the same file
	 * twice still fires `change` and reopens the confirmation.
	 */
	const openFilePicker = () => {
		const input = fileInputRef.current
		if (input) {
			input.value = ''
			input.click()
		}
	}

	// Answered before this page mounted (`MarqueeSessionRoute`) and before the
	// table loads: the role is the permanent half of the answer, so there is
	// nothing to wait for and nothing to show underneath.
	if (!isAllowedToUpload) {
		return (
			<PageShell>
				<CreatorOnlyNotice
					backLabel={t('marquee:creator_only_back_to_events')}
					backTo={routes.MARQUEE_EVENTS.handle.fullPath}
					description={t(
						'marquee:host_lookup_creator_only_description'
					)}
				/>
			</PageShell>
		)
	}

	if (isLoading) {
		return (
			<PageShell>
				<div className='flex flex-col gap-6 l:px-10 px-4 pt-4 pb-10'>
					<Skeleton height={72} />
					<Skeleton height={108} />
					<Skeleton height={180} />
				</div>
			</PageShell>
		)
	}

	if (isError || !status) {
		return (
			<PageShell>
				<div className='l:px-10 px-4 pt-4 pb-10'>
					<div className='flex flex-col items-center gap-2 rounded-airbnb-2xl border border-red-200 bg-red-50 px-6 py-16 text-center'>
						<p className='font-medium text-neutral-900'>
							{t('marquee:host_lookup_error_title')}
						</p>
						<p className='text-neutral-500 text-sm'>
							{t('marquee:host_lookup_error_description')}
						</p>
					</div>
				</div>
			</PageShell>
		)
	}

	const state = hostLookupTableState(status)
	const isEmpty = state === 'empty'

	return (
		<PageShell>
			{/* Title, description, and the two halves of the loop. Both buttons
			    are absent while nothing is loaded: there is no file to download,
			    and the first upload has its own call to action below. */}
			<div className='flex l:flex-row flex-col l:items-start l:justify-between gap-4 l:px-10 px-4 pt-4 pb-6'>
				<div className='flex min-w-0 flex-col gap-2'>
					<h1 className='font-medium text-neutral-900 text-xl'>
						{t('marquee:host_lookup_title')}
					</h1>
					<p className='max-w-3xl text-neutral-500 text-sm'>
						{t('marquee:host_lookup_description')}
					</p>
				</div>

				{!isEmpty && (
					<div className='flex shrink-0 flex-wrap items-center gap-3'>
						<Button
							disabled={download.isPending}
							onClick={() => download.mutate()}
							type='button'
							variant='secondaryOutline'
						>
							<DownloadCloud02Icon />
							{t('marquee:host_lookup_download')}
							{download.isPending && <Spinner size={16} />}
						</Button>
						<Button onClick={openFilePicker} type='button'>
							<UploadIcon />
							{t('marquee:host_lookup_upload')}
						</Button>
					</div>
				)}
			</div>

			<div className='flex flex-col gap-6 l:px-10 px-4 pb-10'>
				{/* The replace-all semantic is the one fact an operator has to
				    hold before touching this screen, so it stays on the page
				    rather than being something dismissible. An empty table
				    raises it to a warning: nothing resolves at all. */}
				{isEmpty ? (
					<InlineBanner
						description={t('marquee:host_lookup_empty_notice_body')}
						icon={
							<AlertTriangleFillIcon className='size-6 text-orange-600' />
						}
						title={t('marquee:host_lookup_empty_notice_title')}
					/>
				) : (
					<InlineBanner
						description={t('marquee:host_lookup_replace_body')}
						icon={
							<InfoFillCircleIcon className='size-6 text-grey-700' />
						}
						title={t('marquee:host_lookup_replace_title')}
					/>
				)}

				{isEmpty ? (
					<>
						<Empty className='rounded-airbnb-2xl border border-grey-200 py-16'>
							<EmptyHeader>
								<EmptyMedia variant='icon'>
									<UploadIcon />
								</EmptyMedia>
								<EmptyTitle>
									{t('marquee:host_lookup_empty_title')}
								</EmptyTitle>
								<EmptyDescription>
									{t('marquee:host_lookup_empty_description')}
								</EmptyDescription>
							</EmptyHeader>
						</Empty>
						<div className='flex justify-end'>
							<Button onClick={openFilePicker} type='button'>
								<UploadIcon />
								{t('marquee:host_lookup_upload_first')}
							</Button>
						</div>
					</>
				) : (
					<HostLookupStatusCard state={state} status={status} />
				)}
			</div>

			<input
				accept='.csv,text/csv'
				className='hidden'
				onChange={(event) =>
					setPendingFile(event.target.files?.[0] ?? null)
				}
				ref={fileInputRef}
				type='file'
			/>

			{/* Keyed on the file so picking another one after a refusal
			    remounts the dialog and its mutation starts clean. */}
			{pendingFile && (
				<HostLookupUploadDialog
					file={pendingFile}
					key={`${pendingFile.name}:${pendingFile.size}:${pendingFile.lastModified}`}
					onClose={() => setPendingFile(null)}
					onPickAnother={openFilePicker}
					status={status}
				/>
			)}
		</PageShell>
	)
}

export { MarqueeHostLookupTable }
